/* =========================================================================
   Typed API client for the ASP.NET Core backend (built in parallel; NOT running
   yet). Everything degrades gracefully: if the player is not logged in OR the
   API is unreachable, callers fall back to GUEST mode (bundled local packs,
   nothing queued) so the app is fully playable and verifiable without a server.

   - JWT access token kept in memory; rotating refresh token in localStorage
     (the iOS wrapper will later persist it natively — §6). Access → memory only.
   - 401 triggers a single refresh-and-retry.
   - Idempotency-Key header on every write (retry-safe, per plan §12.5).
   ========================================================================= */

import { getGuestId } from './guestId';
import { CLIENT_VERSION } from './version';
import { normalizeCareerLevel, normalizeResume } from './serverGame';
import type { GameResumeState, RoundCompleteBody, RoundCompleteResult, ServedQuestionDto } from './serverGame';
import { normalizeRoundOptions, type RoundOptions } from './roundPicker';

const API_BASE = '/api';
const REFRESH_KEY = 'ca_refresh';
const REQUEST_TIMEOUT_MS = 8000;

export interface AuthUser {
  userId: string;
  displayName: string;
  email: string;
  isGuest?: boolean;
}

/** Matches the backend's flat AuthResponse (camelCased by ASP.NET). */
export interface AuthResult {
  userId: string;
  displayName: string;
  email: string | null;
  accessToken: string;
  accessExpiresUtc: string;
  refreshToken: string;
  refreshExpiresUtc: string;
}

/** Matches backend SessionHistoryRow (camelCased). */
export interface HistorySession {
  sessionId: string;
  mode: string;
  startedUtc: string;
  completedUtc: string | null;
  finalBankCents: number | null;
  accuracyPct: number | null;
  medalTier: string | null;
  /** null for sessions completed before db/037 that the backfill couldn't cover
      (older 'online'-mode rows — see 037_session_xp.sql). */
  xpEarned: number | null;
  /** db/051 (usp_Session_History_v2): live round position / bank / hard-mode of
      the row — optional so an older API still type-checks. 0-based round to
      resume at; an incomplete row with a round or a bank is a game in progress. */
  currentRoundIndex?: number;
  bankCents?: number;
  hardMode?: boolean;
}

export interface HistoryResponse {
  sessions: HistorySession[];
}

/** Discriminated result so callers never throw on a dead/absent backend. */
export type ApiResult<T> =
  | { ok: true; data: T }
  | { ok: false; offline: boolean; status?: number; error: string };

let accessToken: string | null = null;
let currentUser: AuthUser | null = null;

function getRefreshToken(): string | null {
  try {
    return localStorage.getItem(REFRESH_KEY);
  } catch {
    return null;
  }
}
function setRefreshToken(t: string | null): void {
  try {
    if (t) localStorage.setItem(REFRESH_KEY, t);
    else localStorage.removeItem(REFRESH_KEY);
  } catch {
    /* private mode / eviction — auth simply won't persist */
  }
}

export function isAuthenticated(): boolean {
  return !!accessToken && !!currentUser;
}
export function getCurrentUser(): AuthUser | null {
  return currentUser;
}

function newIdempotencyKey(): string {
  const c = globalThis.crypto;
  if (c && 'randomUUID' in c) return c.randomUUID();
  return 'idem-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2);
}

interface RawResponse<T> {
  status: number;
  ok: boolean;
  body: T | null;
  networkError: boolean;
}

async function raw<T>(path: string, init: RequestInit, withAuth: boolean): Promise<RawResponse<T>> {
  const headers = new Headers(init.headers || {});
  const method = (init.method || 'GET').toUpperCase();
  if (init.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
  if (withAuth && accessToken) headers.set('Authorization', 'Bearer ' + accessToken);
  if (method !== 'GET' && method !== 'HEAD' && !headers.has('Idempotency-Key')) {
    headers.set('Idempotency-Key', newIdempotencyKey());
  }
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), REQUEST_TIMEOUT_MS);
  try {
    const res = await fetch(API_BASE + path, { ...init, headers, signal: ctrl.signal });
    let body: T | null = null;
    const text = await res.text();
    if (text) {
      try {
        body = JSON.parse(text) as T;
      } catch {
        body = null;
      }
    }
    return { status: res.status, ok: res.ok, body, networkError: false };
  } catch {
    // Network failure, DNS, CORS, timeout — the backend is treated as absent.
    return { status: 0, ok: false, body: null, networkError: true };
  } finally {
    clearTimeout(timer);
  }
}

async function refreshSession(): Promise<boolean> {
  const rt = getRefreshToken();
  if (!rt) return false;
  const r = await raw<{ accessToken: string; refreshToken: string }>(
    '/auth/refresh',
    { method: 'POST', body: JSON.stringify({ refreshToken: rt }) },
    false,
  );
  if (r.ok && r.body) {
    accessToken = r.body.accessToken;
    setRefreshToken(r.body.refreshToken);
    return true;
  }
  if (!r.networkError) {
    // Refresh token is genuinely invalid/expired — clear it.
    accessToken = null;
    currentUser = null;
    setRefreshToken(null);
  }
  return false;
}

/** Authenticated JSON request with a single 401 refresh-and-retry. */
async function request<T>(path: string, init: RequestInit = {}): Promise<ApiResult<T>> {
  let r = await raw<T>(path, init, true);
  if (r.status === 401 && (await refreshSession())) {
    r = await raw<T>(path, init, true);
  }
  if (r.networkError) return { ok: false, offline: true, error: 'Network unavailable' };
  if (!r.ok) return { ok: false, offline: false, status: r.status, error: httpError(r.status) };
  return { ok: true, data: r.body as T };
}

function httpError(status: number): string {
  if (status === 401) return 'Please sign in again.';
  if (status === 403) return 'Not allowed.';
  if (status === 409) return 'That email is already registered.';
  if (status >= 500) return 'Server error — please try again.';
  return 'Request failed (' + status + ').';
}

function adoptAuth(data: AuthResult): AuthUser {
  accessToken = data.accessToken;
  currentUser = { userId: data.userId, displayName: data.displayName, email: data.email ?? '' };
  setRefreshToken(data.refreshToken);
  return currentUser;
}

/* ---------------------------- Auth ---------------------------- */

export async function register(displayName: string, email: string, password: string): Promise<ApiResult<AuthUser>> {
  const r = await request<AuthResult>('/auth/register', {
    method: 'POST',
    body: JSON.stringify({ displayName, email, password }),
  });
  if (r.ok) return { ok: true, data: adoptAuth(r.data) };
  return r;
}

export async function login(email: string, password: string): Promise<ApiResult<AuthUser>> {
  const r = await request<AuthResult>('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
  if (r.ok) return { ok: true, data: adoptAuth(r.data) };
  return r;
}

/* ---- Sign in with Apple (runtime-gated by the server) ---- */
export interface AppleConfig {
  enabled: boolean;
  clientId: string;
}

/** GET /api/auth/apple/config — anonymous. Tells the client whether to show the
    Apple button and with which Services ID. Nulls/offline → treat as disabled. */
export async function getAppleConfig(): Promise<ApiResult<AppleConfig>> {
  const r = await raw<AppleConfig>('/auth/apple/config', { method: 'GET' }, false);
  if (r.networkError) return { ok: false, offline: true, error: 'offline' };
  if (!r.ok || !r.body) return { ok: false, offline: false, status: r.status, error: httpError(r.status) };
  return { ok: true, data: r.body };
}

/** POST /api/auth/apple — exchange Apple's identity token for our own session.
    Response shape matches login/register, so tokens are stored identically. */
export async function appleSignIn(identityToken: string, displayName?: string): Promise<ApiResult<AuthUser>> {
  const r = await request<AuthResult>('/auth/apple', {
    method: 'POST',
    body: JSON.stringify({ identityToken, displayName: displayName ?? null }),
  });
  if (r.ok) return { ok: true, data: adoptAuth(r.data) };
  return r;
}

export async function logout(): Promise<void> {
  const rt = getRefreshToken();
  if (rt) void raw('/auth/logout', { method: 'POST', body: JSON.stringify({ refreshToken: rt }) }, true);
  accessToken = null;
  currentUser = null;
  setRefreshToken(null);
}

/** On boot: if a refresh token survives, silently restore the session.
    Backend /profile/me returns { profile, stats } — we map profile → AuthUser. */
export async function tryRestoreSession(): Promise<boolean> {
  if (!getRefreshToken()) return false;
  if (!(await refreshSession())) return false;
  const me = await request<{ profile: { userId: string; displayName: string; email: string | null } }>('/profile/me');
  if (me.ok && me.data.profile) {
    const p = me.data.profile;
    currentUser = { userId: p.userId, displayName: p.displayName, email: p.email ?? '' };
    return true;
  }
  return false;
}

/** Lightweight reachability probe (used to show the offline banner on Welcome). */
export async function pingApi(): Promise<boolean> {
  const r = await raw('/health', { method: 'GET' }, false);   // backend serves /api/health
  return !r.networkError;
}

/* ---------------------------- Meta / Today ---------------------------- */

/** Row shape of GET /api/meta/today (camel-cased TodayRow, Models.cs:52-61).
    Every field is nullable — the endpoint returns 200 with a null body on days
    with no feast, and individual columns may be null even when a row exists. */
export interface TodayRow {
  feastDayId: number | null;
  title: string | null;
  feastRank: string | null;
  color: string | null;
  didYouKnow: string | null;
  didYouKnowCitation: string | null;
  featuredCategoryId: number | null;
  bonusMultiplier: number | null;
  challengeSeed: number | null;
}

/** GET /api/meta/today?date=YYYY-MM-DD — anonymous raw GET (like getAppleConfig).
    The caller passes the ET date explicitly (the server defaults to UtcNow). A
    200 with a null/empty body is a valid "no feast today" answer → { ok, data:null }.
    Offline/error → ok:false so callers fall back to a bundled generic line. */
export async function getToday(date: string): Promise<ApiResult<TodayRow | null>> {
  const r = await raw<TodayRow>('/meta/today?date=' + encodeURIComponent(date), { method: 'GET' }, false);
  if (r.networkError) return { ok: false, offline: true, error: 'offline' };
  if (r.status === 200) return { ok: true, data: r.body }; // body may be null (no-feast day)
  return { ok: false, offline: false, status: r.status, error: httpError(r.status) };
}

/* ---------------------------- Telemetry ---------------------------- */

export interface ActivityEvent {
  sessionId: string | null;
  eventKey: string;
  propsJson: string | null;
  clientUtc: string;
}

/** Batched activity post; keepalive lets the final flush survive page hide. */
export async function postActivity(events: ActivityEvent[], useKeepalive = false): Promise<boolean> {
  if (!isAuthenticated() || events.length === 0) return true;
  try {
    const res = await fetch(API_BASE + '/log/activity', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + accessToken },
      body: JSON.stringify({ events }),
      keepalive: useKeepalive,
    });
    return res.ok;
  } catch {
    return false;
  }
}

/* One-shot guest first-name upsert. setGuestName() stashes the name; the NEXT
   guest-activity batch carries it as a top-level `guestName` field exactly once
   (the server upserts it). Fail-soft: an old server / 503 just ignores the extra
   field, and we clear the pending name after the first send attempt regardless. */
let pendingGuestName: string | null = null;

/** Capture a guest's first name to travel on the next guest-activity batch. */
export function setGuestName(name: string): void {
  const n = name.trim();
  if (n) pendingGuestName = n;
}

/** Drop any un-sent guest name (logout hygiene — a signed-out shared device must
    not attach the previous owner's name to the next guest batch). */
export function clearGuestName(): void {
  pendingGuestName = null;
}

/** Anonymous guest activity post → /api/log/guest-activity (no Bearer). Powers
    the admin active-guests/day, unique-IP and live-now views. Server caps at 50
    events; the telemetry queue is already bounded to that. A pending guest name
    (set via setGuestName) rides along as a top-level field once, then clears. */
export async function postGuestActivity(
  guestId: string,
  events: ActivityEvent[],
  useKeepalive = false,
): Promise<boolean> {
  if (events.length === 0 && pendingGuestName === null) return true;
  const nameToSend = pendingGuestName;
  const body: Record<string, unknown> = { guestId, events };
  if (nameToSend !== null) body.guestName = nameToSend;
  try {
    const res = await fetch(API_BASE + '/log/guest-activity', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: useKeepalive,
    });
    // Sent (or attempted): don't keep re-sending the name on every future batch.
    if (nameToSend !== null && pendingGuestName === nameToSend) pendingGuestName = null;
    return res.ok;
  } catch {
    return false;
  }
}

/** sendBeacon twin of postGuestActivity, for the page-hide teardown path. The
    body is byte-identical to postGuestActivity (guestId + events + the one-shot
    pendingGuestName ride-along) so the server can't tell the two apart. Unlike
    fetch keepalive, sendBeacon reliably survives an immediate PWA kill because
    the browser owns the send after we hand it off. Returns false (caller falls
    back to fetch keepalive) when sendBeacon is unsupported/refused/throws. We
    clear the pending name ONLY on a true return — a refused beacon must let the
    fetch fallback still carry the name. */
export function beaconGuestActivity(guestId: string, events: ActivityEvent[]): boolean {
  if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') return false;
  const nameToSend = pendingGuestName;
  const body: Record<string, unknown> = { guestId, events };
  if (nameToSend !== null) body.guestName = nameToSend;
  try {
    const ok = navigator.sendBeacon(
      API_BASE + '/log/guest-activity',
      new Blob([JSON.stringify(body)], { type: 'application/json' }),
    );
    // Accepted by the browser's send queue: don't re-send the name next batch.
    if (ok && nameToSend !== null && pendingGuestName === nameToSend) pendingGuestName = null;
    return ok;
  } catch {
    return false;
  }
}

/** sendBeacon twin of postQuestionsServed for the guest page-hide path. Mirrors
    postQuestionsServed's anonymous body exactly ({ guestId, sessionId:null,
    codes }); only used when !isAuthenticated(), so guestId is always the device
    id (never null). Returns false (caller falls back to fetch keepalive) when
    sendBeacon is unsupported/refused/throws. */
export function beaconQuestionsServed(codes: string[]): boolean {
  if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') return false;
  const body = { guestId: getGuestId(), sessionId: null, codes };
  try {
    return navigator.sendBeacon(
      API_BASE + '/log/served',
      new Blob([JSON.stringify(body)], { type: 'application/json' }),
    );
  } catch {
    return false;
  }
}

/** Served-question logging → /api/log/served. Sends the Bearer token when
    authenticated (server keys off the token's user and ignores guestId); sends
    the device guest id otherwise. sessionId is null (no server-driven sessions
    yet). Fire-and-forget — callers requeue on a false return. */
export async function postQuestionsServed(codes: string[], useKeepalive = false): Promise<boolean> {
  if (codes.length === 0) return true;
  const authed = isAuthenticated();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (authed && accessToken) headers.Authorization = 'Bearer ' + accessToken;
  try {
    const res = await fetch(API_BASE + '/log/served', {
      method: 'POST',
      headers,
      body: JSON.stringify({ guestId: authed ? null : getGuestId(), sessionId: null, codes }),
      keepalive: useKeepalive,
    });
    return res.ok;
  } catch {
    return false;
  }
}

/** Live register-screen availability check (anonymous). Nulls = not asked / unknown. */
export interface Availability {
  displayNameAvailable: boolean | null;
  emailAvailable: boolean | null;
}
export async function checkAvailability(displayName?: string, email?: string): Promise<ApiResult<Availability>> {
  const q = new URLSearchParams();
  if (displayName) q.set('displayName', displayName);
  if (email) q.set('email', email);
  const r = await raw<Availability>('/auth/check?' + q.toString(), { method: 'GET' }, false);
  if (r.networkError) return { ok: false, offline: true, error: 'offline' };
  if (!r.ok || !r.body) return { ok: false, offline: false, status: r.status, error: httpError(r.status) };
  return { ok: true, data: r.body };
}

/* ---------------------------- Profile ---------------------------- */

export async function getHistory(): Promise<ApiResult<HistoryResponse>> {
  // backend returns a bare array of SessionHistoryRow
  const r = await request<HistorySession[]>('/profile/history');
  if (r.ok) return { ok: true, data: { sessions: r.data } };
  return r;
}

/** GET /api/profile/me → { profile, stats }. Camel-cased UserProfileRow +
    UserStatsRow (wallet, xp, rank) — drives the account menu header/badge. */
export interface ProfileMe {
  profile: { userId: string; displayName: string; email: string | null; isGuest: boolean };
  stats: {
    xp: number;
    rankLevel: number;
    rankName: string;
    walletBalanceCents: number;
    currentStreak: number;
    bestStreak: number;
    totalSessions: number;
    /** Last ET day a session completed, 'YYYY-MM-DD' (additive, 2026-08-21) — null = never played. */
    lastPlayedDate?: string | null;
  } | null;
}
export async function getProfileMe(): Promise<ApiResult<ProfileMe>> {
  return request<ProfileMe>('/profile/me');
}

/** GET /api/profile/achievements → the user's EARNED achievements. */
export interface EarnedAchievement {
  key: string;
  name: string;
  descr: string;
  icon: string | null;
  family: string | null;
  tier: string | null;
  earnedUtc: string;
  sessionId: string | null;
}
export async function getAchievements(): Promise<ApiResult<EarnedAchievement[]>> {
  return request<EarnedAchievement[]>('/profile/achievements');
}

/* ------------------------------- GTKY (§6) -------------------------------
   POST /api/gtky/submit mirrors one Get-To-Know-You answer into the server bank
   (app.GTKYAnswers), which is what ContentVariants targeting (RequiresGtkyKey)
   and adm.usp_Gtky_AnswerDistribution read. This closes the seam gtky.ts has
   carried since v1.1.0 — localStorage remains the LOCAL source of truth for
   Theo's memory lines, and this is purely the mirror.

   Keyed by MEMORY KEY, not GTKYQuestionId: the bundled bank (data/gtkyData.ts)
   is authored by MemoryKey and has no server ids, so db/049's
   app.usp_GTKY_SaveAnswerByKey resolves key → id server-side.

   AnswerJson is an OBJECT ({"value": …}) rather than a bare JSON scalar:
   app.GTKYAnswers carries CK_GTKYA_Json (ISJSON = 1) and SQL Server 2019's
   ISJSON rejects scalars (verified against the live DB, 2026-08-19).

   FAIL-SOFT BY CONTRACT: guests, offline devices, and keys the server bank does
   not know about all resolve `false`. Callers must never surface this. */
export async function submitGtky(memoryKey: string, value: string): Promise<boolean> {
  if (!isAuthenticated()) return false;
  try {
    const r = await request<null>('/gtky/submit', {
      method: 'POST',
      body: JSON.stringify({ memoryKey, answerJson: JSON.stringify({ value }) }),
    });
    return r.ok;
  } catch {
    return false;
  }
}

/** DELETE /api/profile/me (Apple 5.1.1(v)). Server anonymizes the account and
    revokes refresh tokens; on success we clear local auth state too. 204 → ok. */
export async function deleteAccount(): Promise<ApiResult<null>> {
  if (!isAuthenticated()) return { ok: false, offline: false, error: 'Not signed in' };
  const r = await request<null>('/profile/me', { method: 'DELETE' });
  if (r.ok) {
    accessToken = null;
    currentUser = null;
    setRefreshToken(null);
  }
  return r;
}

/* ---------------------------- Game (server-authoritative) ----------------------------
   Shapes match plan §5. The guest/local path does not call these; they exist so a
   signed-in session can persist server-side once the backend is live. Offline queue /
   Background Sync is deferred to Phase 6 (see TODO). */

export interface CompleteSessionPayload {
  clientVersion: string;
  finalBankCents: number;
  accuracy: number;
  medalTier: string;
  rounds: unknown[];
  achievements: string[];
}

export interface CompleteSessionResult {
  sessionId: string;
  medalTier: string;
  xpAwarded: number;
  achievements: string[];
  /** The ET-day streak AFTER this game counted (CompleteSessionResponse.CurrentStreak).
      Optional: older API builds don't send it. Drives the streak milestone moment. */
  currentStreak?: number;
  /** Streak milestone reward paid on THIS call (db/050): null/absent when no 3/7/14/30
      boundary was crossed or it was already paid for this streak. Hand it to
      ui/streak.ts showStreakMilestone(days, reward) for the reward line. */
  streakMilestone?: StreakMilestoneReward | null;
}

/** Server's StreakMilestoneDto — what the 3/7/14/30-day streak just paid. */
export interface StreakMilestoneReward {
  days: number;
  coinsCents: number;
  itemType: string | null;
  xpBoost: boolean;
  collectibleKey: string | null;
}

/** POST /api/game/complete-summary — only attempted when authenticated & online.
    BETA plays locally and posts a validated summary; the full server-session flow
    (/game/session + /game/answer + /game/complete) is the Phase 3 upgrade. */
export async function completeSession(payload: CompleteSessionPayload): Promise<ApiResult<CompleteSessionResult>> {
  if (!isAuthenticated()) return { ok: false, offline: true, error: 'Guest session — not persisted' };
  return request<CompleteSessionResult>('/game/complete-summary', { method: 'POST', body: JSON.stringify(payload) });
}

export interface FriendlyXpResult {
  xpGained: number;
  totalXp: number;
}

/** POST /api/game/friendly-xp — Friendly Play's only server call (friendlyController.ts
    showEndCard). XP for correct answers ONLY: no session row, no wallet, no streak —
    see GameEndpoints.cs /friendly-xp and usp_Stats_AwardXp (db/038). Guests never call
    this (no UserStats row to credit); the caller gates on isAuthenticated(). */
export async function awardFriendlyXp(correctAnswers: number, answered: number): Promise<ApiResult<FriendlyXpResult>> {
  if (!isAuthenticated()) return { ok: false, offline: true, error: 'Guest session — not tracked' };
  return request<FriendlyXpResult>('/game/friendly-xp', {
    method: 'POST',
    body: JSON.stringify({ correctAnswers, answered }),
  });
}

/* ========================================================================
   v1.1.0 "The Pilgrimage" — quests, inventory, collections, pilgrimage, feast.
   Every helper degrades gracefully: offline / network-error → {ok:false,
   offline:true}, and the server's DB-migration guard (HTTP 503 with body
   {error:'db-migration-pending'} when db/014–019 haven't been run yet) is
   ALSO treated as offline so guests/BETA fall back to bundled local logic.
   Anonymous reads use raw() (like getToday); authed writes use request().
   ======================================================================== */

/** HTTP 503 = the server is up but the v1.1.0 migrations haven't run yet. Treat
    it exactly like being offline so callers use their bundled/local fallback. */
function isMigrationPending(status: number): boolean {
  return status === 503;
}

/** Wrap a raw() response into an ApiResult, mapping network + 503 → offline. */
function anonResult<T>(r: RawResponse<T>): ApiResult<T> {
  if (r.networkError) return { ok: false, offline: true, error: 'offline' };
  if (isMigrationPending(r.status)) return { ok: false, offline: true, error: 'db-migration-pending' };
  if (r.status < 200 || r.status >= 300) return { ok: false, offline: false, status: r.status, error: httpError(r.status) };
  return { ok: true, data: r.body as T };
}

/** Same mapping as anonResult but for the authed request() path (which already
    collapses network errors) — used to normalize 503 to offline post-hoc. */
function migrationAware<T>(r: ApiResult<T>): ApiResult<T> {
  if (!r.ok && !r.offline && r.status === 503) return { ok: false, offline: true, error: 'db-migration-pending' };
  return r;
}

/* ---------------------------- Quests (db/014) ---------------------------- */

/** One row of GET /api/quests/today (camel-cased QuestDto). When authed the
    server merges the caller's state (progress/completedUtc); anon rows carry
    progress 0 / completedUtc null. */
export interface QuestTodayRow {
  key: string;
  title: string;
  descr: string | null;
  icon: string | null;
  targetCount: number;
  progress: number;
  completedUtc: string | null;
  rewardJson: string | null;
}

/** GET /api/quests/today?date=YYYY-MM-DD — anonymous, honors an optional Bearer.
    Caller passes the ET calendar date explicitly. 503/offline → ok:false so the
    quest engine uses its bundled 6-quest fallback. */
export async function getQuestsToday(dateIso: string): Promise<ApiResult<QuestTodayRow[]>> {
  const r = await raw<QuestTodayRow[]>('/quests/today?date=' + encodeURIComponent(dateIso), { method: 'GET' }, false);
  return anonResult(r);
}

export interface QuestRecordBody {
  questKey: string;
  periodKey: string; // 'YYYY-MM-DD' ET
  delta: number;
  idempotencyKey: string;
}
export interface QuestRecordResult {
  progress: number;
  completed: boolean;
}

/** POST /api/quests/record — AUTHED, idempotent by idempotencyKey. Guests skip
    (the client evaluates + persists locally). Fire-and-forget at call sites. */
export async function recordQuest(body: QuestRecordBody): Promise<ApiResult<QuestRecordResult>> {
  if (!isAuthenticated()) return { ok: false, offline: true, error: 'guest — local only' };
  return migrationAware(
    await request<QuestRecordResult>('/quests/record', { method: 'POST', body: JSON.stringify(body) }),
  );
}

/* --------------------------- Inventory (db/015) --------------------------- */

/** Camel-cased InventoryItemDto — the merged catalog + the caller's quantities. */
export interface InventoryRow {
  itemType: string;
  name: string;
  icon: string | null;
  descr: string | null;
  qty: number;
}

/** GET /api/inventory — AUTHED. Guests keep inventory in localStorage only. */
export async function getInventory(): Promise<ApiResult<InventoryRow[]>> {
  if (!isAuthenticated()) return { ok: false, offline: true, error: 'guest — local only' };
  return migrationAware(await request<InventoryRow[]>('/inventory'));
}

/* ------------------------ Game session + break-result ------------------------ */

/** POST /api/game/session — AUTHED. Opens a server session: the id tags break
    results + telemetry, `careerLevel` drives the difficulty ramp and the round
    intro's "Level N" line, and `resume` (when present) is the server-side save
    point that supersedes the localStorage snapshot. Guests get no server session
    and keep the fully-offline bundled loop. A server that predates Phase 1 simply
    yields careerLevel 0 / resume null — every caller treats that as "no data". */
export interface StartGameSessionResult {
  sessionId: string;
  careerLevel: number;
  resume: GameResumeState | null;
}
export async function startGameSession(totalRounds = 5): Promise<ApiResult<StartGameSessionResult>> {
  if (!isAuthenticated()) return { ok: false, offline: true, error: 'guest — no server session' };
  const r = await request<{ sessionId: string; careerLevel?: number; resume?: unknown }>('/game/session', {
    method: 'POST',
    body: JSON.stringify({ clientBuild: CLIENT_VERSION }),
  });
  if (r.ok) {
    return {
      ok: true,
      data: {
        sessionId: r.data.sessionId,
        careerLevel: normalizeCareerLevel(r.data.careerLevel),
        resume: normalizeResume(r.data.resume, totalRounds),
      },
    };
  }
  return migrationAware(r);
}

/** GET /api/game/session/active — AUTHED. The open save point, or null. Used by
    the home Start CTA path when no session has been opened yet this boot. */
export async function getActiveGameSession(totalRounds = 5): Promise<ApiResult<GameResumeState | null>> {
  if (!isAuthenticated()) return { ok: false, offline: true, error: 'guest — no server session' };
  const r = migrationAware(await request<unknown>('/game/session/active'));
  if (!r.ok) return r;
  return { ok: true, data: normalizeResume(r.data, totalRounds) };
}

/** GET /api/game/progress — AUTHED. Career level / XP block for the home badge.
    Body shaping is progressClient.normalizeProgress's job; this just carries auth. */
export async function getGameProgress(): Promise<ApiResult<unknown>> {
  if (!isAuthenticated()) return { ok: false, offline: true, error: 'guest — no progress' };
  return migrationAware(await request<unknown>('/game/progress'));
}

/** One row of GET /api/game/prayer-break (camel-cased, wrapping db/042's
    app.usp_PrayerBreak_Next — least-seen, then least-recently-seen rotation).
    No reward fields exist by design: prayer is never gamified. */
export interface PrayerBreakDto {
  code: string;
  title: string;
  line: string;
  attribution: string;
  fullText: string;
  holdSeconds: number;
}

/** GET /api/game/prayer-break — AUTHED. The next moment of quiet for this user.
    Guests, offline players, 404 (endpoint not deployed yet) and migration-pending
    all collapse to ok:false, and prayerBreak.ts then serves its bundled six-row
    rotation — the moment always lands. */
export async function getPrayerBreak(): Promise<ApiResult<PrayerBreakDto | null>> {
  if (!isAuthenticated()) return { ok: false, offline: true, error: 'guest — bundled rotation' };
  return migrationAware(await request<PrayerBreakDto | null>('/game/prayer-break'));
}

/** GET /api/game/round-options — AUTHED. Whether the "What shall we study
    today?" category picker is unlocked (server rule: ≥ 10 lifetime completed
    rounds) plus the pickable groups with live question counts. Guest / offline /
    401 / 404 / migration-pending → ok:false, and a malformed body normalizes to
    locked — every failure mode means "show nothing" (roundPicker.ts). */
export async function getRoundOptions(): Promise<ApiResult<RoundOptions>> {
  if (!isAuthenticated()) return { ok: false, offline: true, error: 'guest — no round options' };
  const r = migrationAware(await request<unknown>('/game/round-options'));
  if (!r.ok) return r;
  return { ok: true, data: normalizeRoundOptions(r.data) };
}

/** POST /api/game/round-questions — AUTHED. The round's questions, chosen server
    side from the caller's career level + question history (spaced repetition).
    `groupKey` (optional) is the round picker's category choice — null/omitted =
    Mixed, and the server ignores unknown keys (serves Mixed), so the client
    never pre-validates. Any failure collapses to offline/ok:false and the
    caller plays bundled packs. */
export async function getRoundQuestions(body: {
  sessionId: string;
  roundIndex: number;
  count: number;
  hardMode: boolean;
  groupKey?: string | null;
}): Promise<ApiResult<{ questions: ServedQuestionDto[] }>> {
  if (!isAuthenticated()) return { ok: false, offline: true, error: 'guest — bundled packs' };
  return migrationAware(
    await request<{ questions: ServedQuestionDto[] }>('/game/round-questions', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  );
}

export interface GameAnswerBody {
  sessionId: string;
  stableCode: string;
  selectedIndex: number; // -1 = timed out
  correct: boolean;
  elapsedMs: number;
  /** Cents this answer moved the bank by (engine AnswerResult.deltaCents). */
  valueCents: number;
}

/** POST /api/game/answer — AUTHED, fire-and-forget via answerQueue.ts. Writes
    app.UserQuestionHistory, which is what stops questions silently repeating. */
export async function postGameAnswer(body: GameAnswerBody): Promise<ApiResult<unknown>> {
  if (!isAuthenticated()) return { ok: false, offline: true, error: 'guest — not recorded' };
  return migrationAware(await request<unknown>('/game/answer', { method: 'POST', body: JSON.stringify(body) }));
}

/** POST /api/game/round-complete — AUTHED. The per-round save point + XP award
    (PLAN-progression §2: a one-round lunch break still moves the bar). */
export async function postRoundComplete(body: RoundCompleteBody): Promise<ApiResult<RoundCompleteResult>> {
  if (!isAuthenticated()) return { ok: false, offline: true, error: 'guest — not persisted' };
  return migrationAware(
    await request<RoundCompleteResult>('/game/round-complete', { method: 'POST', body: JSON.stringify(body) }),
  );
}

export interface BreakResultBody {
  sessionId: string;
  gameKey: string; // 'wheel' for the bonus wheel
  wheelIndex?: number;
  score?: number;
  idempotencyKey?: string;
}
export interface BreakItemGrant {
  type: string;
  qty: number;
}
export interface BreakResultResult {
  rewardCents: number;
  jackpot: boolean;
  breakPlayId: number;
  item: BreakItemGrant | null;
}

/** POST /api/game/break-result — AUTHED. Records a bonus-break outcome and (for
    wheel item slots, db/015) grants the item server-side, returning it. */
export async function postBreakResult(body: BreakResultBody): Promise<ApiResult<BreakResultResult>> {
  if (!isAuthenticated()) return { ok: false, offline: true, error: 'guest — local grant only' };
  return migrationAware(
    await request<BreakResultResult>('/game/break-result', { method: 'POST', body: JSON.stringify(body) }),
  );
}

/* ------------------------- Arcade rewards (db/023) ------------------------- */

export interface ArcadeResultBody {
  gameKey: string; // 'manna' | 'match3' | 'maryMatch3' | 'wheel'
  winningsCents: number; // the arcade play's tally (server clamps + daily-caps)
  idempotencyKey: string; // unique per play; also the ledger dedup key
}
export interface ArcadeResultResult {
  creditedCents: number; // what actually hit the wallet (0 if the daily cap is spent)
  remainingTodayCents: number; // headroom left under today's arcade cap
  dailyCapCents: number;
  walletBalanceCents: number; // wallet after this credit
}

/** POST /api/game/arcade-result — AUTHED. Credits a post-game Arcade play's
    winnings to the wallet, server-authoritatively clamped to a per-day cap so the
    infinitely-replayable arcade can't farm points. Guests never persist (the arcade
    stays pure practice for them). Migration-aware: until db/023 + the endpoint ship,
    503/404 collapses to offline and the caller simply shows no credit (honest — it
    never claims points that didn't persist). */
export async function postArcadeResult(body: ArcadeResultBody): Promise<ApiResult<ArcadeResultResult>> {
  if (!isAuthenticated()) return { ok: false, offline: true, error: 'guest — arcade not persisted' };
  return migrationAware(
    await request<ArcadeResultResult>('/game/arcade-result', { method: 'POST', body: JSON.stringify(body) }),
  );
}

/* --------------------------- Collections (db/016) --------------------------- */

export interface CollectionItem {
  key: string;
  title: string;
  icon: string | null;
  earnedUtc: string | null;
}
export interface CollectionSet {
  key: string;
  title: string;
  icon: string | null;
  items: CollectionItem[];
}

/** GET /api/collections — anonymous; merged earned state when authed. */
export async function getCollections(): Promise<ApiResult<CollectionSet[]>> {
  const r = await raw<CollectionSet[]>('/collections', { method: 'GET' }, false);
  return anonResult(r);
}

/** POST /api/collections/grant — AUTHED, 204 on success. */
export async function grantCollectible(body: { itemKey: string }): Promise<ApiResult<null>> {
  if (!isAuthenticated()) return { ok: false, offline: true, error: 'guest — local only' };
  return migrationAware(await request<null>('/collections/grant', { method: 'POST', body: JSON.stringify(body) }));
}

/* --------------------------- Pilgrimage (db/017) --------------------------- */

export interface PilgrimageStep {
  key: string;
  title: string;
  theoLineKey: string | null;
  gateCriteriaJson: string | null;
  rewardJson: string | null;
  nodeX: number;
  nodeY: number;
  completedUtc: string | null;
}
export interface Pilgrimage {
  key: string;
  title: string;
  descr: string | null;
  mapAssetKey: string | null;
  introAssetKey: string | null;
  completionRewardJson: string | null;
  steps: PilgrimageStep[];
}

/** GET /api/pilgrimage/{key} — anonymous; merged completion state when authed. */
export async function getPilgrimage(key: string): Promise<ApiResult<Pilgrimage | null>> {
  const r = await raw<Pilgrimage>('/pilgrimage/' + encodeURIComponent(key), { method: 'GET' }, false);
  if (r.networkError) return { ok: false, offline: true, error: 'offline' };
  if (isMigrationPending(r.status)) return { ok: false, offline: true, error: 'db-migration-pending' };
  if (r.status === 200) return { ok: true, data: r.body }; // body may be null (unknown key)
  return { ok: false, offline: false, status: r.status, error: httpError(r.status) };
}

/** POST /api/pilgrimage/step-complete — AUTHED. 204 ok; 409 = a prior step is
    still incomplete (server validates order) → surfaced as a non-offline error. */
export async function completePilgrimageStep(body: { stepKey: string }): Promise<ApiResult<null>> {
  if (!isAuthenticated()) return { ok: false, offline: true, error: 'guest — local only' };
  return migrationAware(await request<null>('/pilgrimage/step-complete', { method: 'POST', body: JSON.stringify(body) }));
}

/* ----------------------------- Feast (db/018) ----------------------------- */

export interface FeastQuiz {
  q: string;
  options: string[];
  answerIdx: number;
}
export interface FeastMoment {
  feastDayId: number;
  title: string;
  feastRank: string | null;
  color: string | null;
  storyText: string | null;
  quiz: FeastQuiz | null;
}

/** GET /api/meta/feast-moment?date=YYYY-MM-DD — anonymous. A 200 with a null body
    is a valid "no feast today" answer → { ok, data:null }. */
export async function getFeastMoment(dateIso: string): Promise<ApiResult<FeastMoment | null>> {
  const r = await raw<FeastMoment>('/meta/feast-moment?date=' + encodeURIComponent(dateIso), { method: 'GET' }, false);
  if (r.networkError) return { ok: false, offline: true, error: 'offline' };
  if (isMigrationPending(r.status)) return { ok: false, offline: true, error: 'db-migration-pending' };
  if (r.status === 200) return { ok: true, data: r.body }; // body may be null (no feast)
  return { ok: false, offline: false, status: r.status, error: httpError(r.status) };
}

/** POST /api/feast/badge — AUTHED, 204. Grants the Feast Days collection badge. */
export async function grantFeastBadge(body: { feastKey: string }): Promise<ApiResult<null>> {
  if (!isAuthenticated()) return { ok: false, offline: true, error: 'guest — local only' };
  return migrationAware(await request<null>('/feast/badge', { method: 'POST', body: JSON.stringify(body) }));
}

/* ------------------- Gallery / Monastery restoration (§9) -------------------
   These GET/POST helpers wrap the EXISTING v1.0.5 endpoints (MetaEndpoints.cs:19,26
   + GalleryEndpoints.cs:12) which the TS client never wired; shapes are the
   camel-cased GalleryItemRow / CollectionRow (Models.cs:105,170) + the purchase
   response { ledgerEntryId, walletBalanceCents }. The catalog is anonymous (guests
   browse + see prices); my-collection + purchase are authed (wallet is server-side,
   so guests never fake-purchase). */

/** Camel-cased GalleryItemRow — the purchasable-piece catalog (anonymous). */
export interface GalleryItem {
  galleryItemId: number;
  key: string;
  name: string;
  descr: string | null;
  priceCents: number;
  icon: string | null;
  hasNamePlate: boolean;
  sortOrder: number;
}

/** GET /api/meta/gallery — anonymous catalog of purchasable chapel pieces. */
export async function getGallery(): Promise<ApiResult<GalleryItem[]>> {
  const r = await raw<GalleryItem[]>('/meta/gallery', { method: 'GET' }, false);
  return anonResult(r);
}

/** Camel-cased CollectionRow — one owned gallery piece (usp_Meta_MyCollection). */
export interface OwnedGalleryItem {
  collectionId: number;
  key: string;
  name: string;
  icon: string | null;
  namePlate: string | null;
  purchasedUtc: string;
  displayOrder: number;
}

/** GET /api/meta/my-collection — AUTHED. The pieces the account owns (drive the
    monastery restoration stage). Guests own nothing server-side. */
export async function getMyCollection(): Promise<ApiResult<OwnedGalleryItem[]>> {
  if (!isAuthenticated()) return { ok: false, offline: true, error: 'guest — local only' };
  return migrationAware(await request<OwnedGalleryItem[]>('/meta/my-collection'));
}

export interface GalleryPurchaseBody {
  galleryItemKey: string;
  namePlate?: string | null;
  beadsToSpend?: number;
  idempotencyKey?: string;
}
export interface GalleryPurchaseResult {
  ledgerEntryId: number;
  walletBalanceCents: number | null;
}

/** POST /api/gallery/purchase — AUTHED. Spends from the wallet (server-side,
    transactional), optionally discounting with rosary beads (beadsToSpend). A 400
    surfaces as a non-offline error (insufficient funds / beads / item not found). */
export async function purchaseGallery(body: GalleryPurchaseBody): Promise<ApiResult<GalleryPurchaseResult>> {
  if (!isAuthenticated()) return { ok: false, offline: true, error: 'guest — sign in to purchase' };
  return migrationAware(
    await request<GalleryPurchaseResult>('/gallery/purchase', { method: 'POST', body: JSON.stringify(body) }),
  );
}

/* ------------------------------ Notifications ------------------------------ */

/** POST /api/notifications/register-apns — AUTHED. pushToken is the raw APNs hex
    device token delivered by the iOS wrapper's `push-token` bridge event. */
export async function postApnsRegistration(
  pushToken: string,
  topics: string[],
  ianaTimeZone?: string,
): Promise<ApiResult<null>> {
  if (!isAuthenticated()) return { ok: false, offline: true, error: 'guest — sign in for notifications' };
  return migrationAware(
    await request<null>('/notifications/register-apns', {
      method: 'POST',
      body: JSON.stringify({ pushToken, topics, ianaTimeZone }),
    }),
  );
}

/** PUT /api/notifications/preferences — AUTHED. Applies to every device on the account. */
export async function putNotificationPreferences(topics: string[]): Promise<ApiResult<null>> {
  if (!isAuthenticated()) return { ok: false, offline: true, error: 'guest — sign in for notifications' };
  return migrationAware(
    await request<null>('/notifications/preferences', { method: 'PUT', body: JSON.stringify({ topics }) }),
  );
}

/** DELETE /api/notifications/unregister — AUTHED. Drops this device's row (logout / permission revoked). */
export async function deleteNotificationDevice(pushToken: string): Promise<ApiResult<null>> {
  if (!isAuthenticated()) return { ok: false, offline: true, error: 'guest — sign in for notifications' };
  return migrationAware(
    await request<null>('/notifications/unregister', { method: 'DELETE', body: JSON.stringify({ pushToken }) }),
  );
}

/* ---------------------------- Feedback (db/035) ---------------------------- */

export interface FeedbackBody {
  guestId?: string;
  email?: string;
  category: string;
  message: string;
  clientVersion?: string;
  screen?: string;
}

export interface FeedbackResult {
  ticketId: number;
}

/** Best-effort friendly text out of an error body. The endpoint may answer with
    `{error}`, `{message}` or an ASP.NET ProblemDetails `{detail|title}` — take
    whichever is there so the rate-limit line the server wrote reaches the player
    verbatim instead of a generic "Request failed (429)". */
function serverMessage(body: unknown): string | null {
  if (!body || typeof body !== 'object') return null;
  const b = body as Record<string, unknown>;
  // `message` first on purpose: the 429 body is { error: "RateLimited",
  // message: "<the warm line>" } — the human sentence must win over the code.
  for (const k of ['message', 'error', 'detail', 'title']) {
    const v = b[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return null;
}

/** POST /api/feedback — ANONYMOUS-CAPABLE (mirrors /log/guest-activity): works
    for guests AND signed-in players. The Bearer token rides along when we have
    one (server attributes the ticket to the account and already knows the
    email); guests send their device guestId instead and may add a reply-to
    email. A 429 (rate limit: 5/hour) — or a 400 if the server chose that shape —
    carries a friendly server-written line, which we surface as `error` so the
    caller can show it as-is. Network/503-migration collapse to offline:true so
    the caller keeps the player's draft instead of showing a hard failure. */
export async function postFeedback(body: FeedbackBody): Promise<ApiResult<FeedbackResult>> {
  const authed = isAuthenticated();
  const payload: Record<string, unknown> = { ...body };
  if (!authed) payload.guestId = body.guestId ?? getGuestId();
  const init: RequestInit = { method: 'POST', body: JSON.stringify(payload) };

  let r = await raw<FeedbackResult>('/feedback', init, authed);
  // Same single refresh-and-retry `request()` does — an expired token must not
  // cost the player their note.
  if (authed && r.status === 401 && (await refreshSession())) r = await raw<FeedbackResult>('/feedback', init, authed);

  if (r.networkError) return { ok: false, offline: true, error: 'offline' };
  if (isMigrationPending(r.status)) return { ok: false, offline: true, error: 'db-migration-pending' };
  if (r.status === 429 || r.status === 400) {
    return {
      ok: false,
      offline: false,
      status: r.status,
      error: serverMessage(r.body) ?? 'Thanks — we already have your earlier notes. Please try again a little later.',
    };
  }
  if (!r.ok || !r.body) return { ok: false, offline: false, status: r.status, error: httpError(r.status) };
  return { ok: true, data: r.body };
}

/* --------------------------- Leaderboard (db/033) --------------------------- */

/** One REAL ranked player from GET /api/leaderboard/top. Rank is 1-based over
    real players only — the client re-ranks after prepending its seeded entries. */
export interface LeaderboardApiEntry {
  rank: number;
  userId: string;
  displayName: string;
  xp: number;
  rankLevel: number;
  rankName: string | null;
  rankIcon: string | null;
}

export interface LeaderboardResponse {
  entries: LeaderboardApiEntry[];
}

/** GET /api/leaderboard/top — ANONYMOUS (guests browse the board too). */
export async function getLeaderboardTop(limit = 50): Promise<ApiResult<LeaderboardResponse>> {
  const r = await raw<LeaderboardResponse>(`/leaderboard/top?limit=${encodeURIComponent(String(limit))}`, { method: 'GET' }, false);
  return anonResult(r);
}

/* ------------------- Economy sinks: wings + Poor Box (db/047) ------------------- */

/** One monastery expansion wing (GET /api/monastery/wings). `costCents` is the
    SERVER's price — it is display-only here; /build never sends an amount. */
export interface MonasteryWing {
  key: string;
  name: string;
  descr: string | null;
  icon: string | null;
  costCents: number;
  blessing: string | null;
  sortOrder: number;
  built: boolean;
  /** Every earlier wing is already raised (the ladder is strictly sequential). */
  unlocked: boolean;
  builtUtc: string | null;
}

export interface MonasteryWingsResponse {
  wings: MonasteryWing[];
  walletBalanceCents: number;
  builtCount: number;
  totalCount: number;
  investedCents: number;
  totalCostCents: number;
}

/** GET /api/monastery/wings — AUTHED. The wallet is server-side, so guests get a
    sign-in nudge rather than a local balance. */
export async function getMonasteryWings(): Promise<ApiResult<MonasteryWingsResponse>> {
  if (!isAuthenticated()) return { ok: false, offline: true, error: 'guest — sign in to expand the monastery' };
  return migrationAware(await request<MonasteryWingsResponse>('/monastery/wings'));
}

export interface BuildWingResult {
  wingKey: string;
  name: string;
  blessing: string | null;
  spentCents: number;
  walletBalanceCents: number;
  /** false when this was a replay of a build that already happened. */
  newlyBuilt: boolean;
  ledgerEntryId: number;
}

/** POST /api/monastery/build — AUTHED. Sends a KEY, never a price: the server
    owns the cost and debits transactionally (400 on insufficient funds / a wing
    whose predecessors are still unbuilt). */
export async function buildMonasteryWing(wingKey: string): Promise<ApiResult<BuildWingResult>> {
  if (!isAuthenticated()) return { ok: false, offline: true, error: 'guest — sign in to expand the monastery' };
  return migrationAware(
    await request<BuildWingResult>('/monastery/build', { method: 'POST', body: JSON.stringify({ wingKey }) }),
  );
}

/** One of the monastery's in-fiction works of mercy. */
export interface AlmsCause {
  key: string;
  name: string;
  descr: string | null;
  icon: string | null;
}

/** A lifetime-giving milestone: a title and a Brother Theo line. There is no
    reward here and there must never be one (PLAN-gameplay-v2 §1 reverence). */
export interface AlmsMilestone {
  key: string;
  title: string;
  atCents: number;
  note: string;
}

export interface AlmsResponse {
  causes: AlmsCause[];
  lifetimeCents: number;
  donationCount: number;
  firstGiftUtc: string | null;
  walletBalanceCents: number;
  milestone: AlmsMilestone | null;
  nextMilestone: AlmsMilestone | null;
  minDonationCents: number;
  maxDonationCents: number;
}

/** GET /api/alms — AUTHED. The Poor Box panel in one call. */
export async function getAlms(): Promise<ApiResult<AlmsResponse>> {
  if (!isAuthenticated()) return { ok: false, offline: true, error: 'guest — sign in to give' };
  return migrationAware(await request<AlmsResponse>('/alms'));
}

export interface DonateResult {
  causeKey: string;
  causeName: string;
  blessing: string | null;
  amountCents: number;
  walletBalanceCents: number;
  lifetimeCents: number;
  donationCount: number;
  newlyRecorded: boolean;
  /** Non-null only when THIS gift crossed a rung — the milestone thank-you. */
  milestoneReached: AlmsMilestone | null;
  milestone: AlmsMilestone | null;
  nextMilestone: AlmsMilestone | null;
}

/** POST /api/alms/donate — AUTHED. Debits server-side and returns warmth only:
    a blessing, the new lifetime total, and a milestone when one is crossed. */
export async function donateAlms(causeKey: string, amountCents: number): Promise<ApiResult<DonateResult>> {
  if (!isAuthenticated()) return { ok: false, offline: true, error: 'guest — sign in to give' };
  return migrationAware(
    await request<DonateResult>('/alms/donate', {
      method: 'POST',
      body: JSON.stringify({ causeKey, amountCents }),
    }),
  );
}
